/**
 * businessImpactCalculations.ts - Business impact mapping utilities
 * 
 * This file now serves as a facade for the UnifiedScoringService's
 * business impact functionality, maintaining backward compatibility.
 */

import { ScoreCategory } from '../scoring/calculator';
import { UnifiedScoringService } from '../services/UnifiedScoringService';

// Re-export BusinessCriteria from UnifiedScoringService
export type { BusinessCriteria } from '../services/UnifiedScoringService';

// Get singleton instance
const scoringService = UnifiedScoringService.getInstance();

/**
 * Maps 4 base scoring categories to 5 business-focused criteria
 * This is the core calculation that should be consistent across all screens
 */
export function mapToBusinessCriteria(scores: ScoreCategory) {
  return scoringService.mapToBusinessCriteria(scores);
}

/**
 * Calculate overall business score from the 5 criteria
 */
export function calculateBusinessScore(criteria: import('../services/UnifiedScoringService').BusinessCriteria): number {
  return scoringService.calculateBusinessScore(criteria);
}

/**
 * Get business impact labels for display
 */
export function getBusinessImpactLabels(): Record<keyof import('../services/UnifiedScoringService').BusinessCriteria, string> {
  return scoringService.getBusinessImpactLabels();
}

/**
 * Get color class based on score
 */
export function getScoreColorClass(score: number): string {
  if (score >= 80) return 'text-green-600 dark:text-green-400';
  if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
  if (score >= 40) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Get background color class based on score
 */
export function getScoreBgColorClass(score: number): string {
  if (score >= 80) return 'bg-green-100 dark:bg-green-900/20';
  if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/20';
  if (score >= 40) return 'bg-orange-100 dark:bg-orange-900/20';
  return 'bg-red-100 dark:bg-red-900/20';
}

/**
 * Get progress bar color class based on score
 */
export function getProgressColorClass(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

/**
 * Format score for display with consistent decimal places
 */
export function formatScore(score: number): string {
  if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) {
    return '0';
  }
  return Math.round(score).toString();
}

/**
 * Validate business criteria object
 */
export function validateBusinessCriteria(criteria: any): criteria is import('../services/UnifiedScoringService').BusinessCriteria {
  if (!criteria || typeof criteria !== 'object') {
    return false;
  }
  
  const requiredFields = [
    'regulatoryCompliance',
    'operationalResiliency', 
    'customerTrust',
    'ipProtection',
    'productIntegrity'
  ];
  
  return requiredFields.every(field => 
    field in criteria && 
    typeof criteria[field] === 'number' &&
    !isNaN(criteria[field]) &&
    isFinite(criteria[field])
  );
}

/**
 * Get risk level label based on score
 */
export function getRiskLevel(score: number): string {
  if (score >= 80) return 'Low Risk';
  if (score >= 60) return 'Moderate Risk';
  if (score >= 40) return 'High Risk';
  return 'Critical Risk';
}

/**
 * Get risk level color classes
 */
export function getRiskLevelColors(score: number): { text: string; bg: string } {
  if (score >= 80) {
    return {
      text: 'text-green-700 dark:text-green-300',
      bg: 'bg-green-50 dark:bg-green-900/30'
    };
  }
  if (score >= 60) {
    return {
      text: 'text-yellow-700 dark:text-yellow-300',
      bg: 'bg-yellow-50 dark:bg-yellow-900/30'
    };
  }
  if (score >= 40) {
    return {
      text: 'text-orange-700 dark:text-orange-300',
      bg: 'bg-orange-50 dark:bg-orange-900/30'
    };
  }
  return {
    text: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-50 dark:bg-red-900/30'
  };
}

/**
 * Calculate score delta (change) with proper validation
 */
export function calculateScoreDelta(currentScore: number, previousScore: number): {
  value: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
} {
  const current = typeof currentScore === 'number' && !isNaN(currentScore) ? currentScore : 0;
  const previous = typeof previousScore === 'number' && !isNaN(previousScore) ? previousScore : 0;
  
  const delta = current - previous;
  const percentage = previous > 0 ? (delta / previous) * 100 : 0;
  
  return {
    value: Math.round(delta),
    percentage: Math.round(percentage),
    trend: delta > 0 ? 'up' : delta < 0 ? 'down' : 'stable'
  };
}

/**
 * Map impact scores to business criteria (alias for mapToBusinessCriteria)
 */
export function mapImpactToBusinessCriteria(impact: ScoreCategory) {
  return mapToBusinessCriteria(impact);
}

/**
 * Get icon for business impact category
 */
export function getBusinessImpactIcon(category: keyof import('../services/UnifiedScoringService').BusinessCriteria): string {
  const icons: Record<keyof import('../services/UnifiedScoringService').BusinessCriteria, string> = {
    regulatoryCompliance: '⚖️',
    operationalResiliency: '🛡️',
    customerTrust: '🤝',
    ipProtection: '🔒',
    productIntegrity: '✅'
  };
  return icons[category] || '📊';
}

/**
 * Get color for business impact category based on score
 */
export function getBusinessImpactColor(score: number, isLight?: boolean): string {
  return getScoreColorClass(score);
}